import React from "react";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  Label,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../../ThemeContext";
import { useAppSelector } from "../../../../redux/store";
import { reportSliceSelectors } from "../../../../redux/report/report.selector";
import { ReportWidget } from "../ReportWidget";
import { Flex } from "../../../../components/Flex";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { EntityIconsByActivityKind } from "../../../../utils/GlobalIcons";
import { ActivityEntityKind } from "../../../../model/activity/types/ActivityEntityKind.enum";
import {
  getKindColor,
  getKindLabel,
  SERIES_TO_KIND,
} from "../../../../utils/Reports";
import { Info } from "../../../../components/Info";

// The dashboard already puts the chart inside its own card
const ChartFrame: React.FC<{
  asWidget?: boolean;
  children: React.ReactNode;
}> = ({ asWidget, children }) =>
  asWidget ? (
    <>{children}</>
  ) : (
    <ReportWidget iconColor="main" title="">
      {children}
    </ReportWidget>
  );

type Props = {
  displayAsWidget?: boolean;
};

export const ReportsByUserChartStackWithClosed: React.FC<Props> = ({
  displayAsWidget,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  // Counted server-side now: deriving these three totals on the client was the
  // only reason the response used to carry every activity event.
  const data =
    useAppSelector(reportSliceSelectors.selectReport)?.metricsByUser || [];

  const isEmpty = data.every((user) =>
    [user.dealsClosed, user.entriesConverted, user.tasksDone].every(
      (val) => !val,
    ),
  );

  return (
    <Flex flexDirection="column" gap="m">
      {!displayAsWidget ? (
        <Info info={t("reports.tabs.chart.stackWithClosed.title")} />
      ) : null}

      <ChartFrame asWidget={displayAsWidget}>
        <Flex
          style={{ width: "100%", height: displayAsWidget ? "15rem" : "40dvh" }}
        >
          <ResponsiveContainer>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              {isEmpty && <Label value={t("common.empty")} position="center" />}
              <YAxis allowDecimals={false} />

              {!isEmpty ? (
                <Tooltip
                  wrapperStyle={{ zIndex: 999999 }}
                  separator=""
                  formatter={(value, _name, item) => {
                    const seriesKey = String(item.dataKey);
                    const kind = SERIES_TO_KIND[seriesKey];
                    return (
                      <Flex alignItems="center" flexDirection="row" gap="s">
                        {kind ? (
                          <AtiraIcon
                            icon={EntityIconsByActivityKind[kind]}
                            color={
                              getKindColor(kind, theme) as keyof typeof theme
                            }
                          />
                        ) : null}
                        <span>{value}</span>
                      </Flex>
                    );
                  }}
                />
              ) : null}

              <Legend />

              <Bar
                dataKey="entriesConverted"
                fill={getKindColor(ActivityEntityKind.ENTRY, theme)}
                name={getKindLabel(ActivityEntityKind.ENTRY, t)}
                radius={[10, 10, 0, 0]}
              />

              <Bar
                dataKey="dealsClosed"
                fill={getKindColor(ActivityEntityKind.KANBAN_CARD, theme)}
                name={getKindLabel(ActivityEntityKind.KANBAN_CARD, t)}
                radius={[10, 10, 0, 0]}
              />
              <Bar
                dataKey="tasksDone"
                fill={getKindColor(ActivityEntityKind.TASK, theme)}
                name={getKindLabel(ActivityEntityKind.TASK, t)}
                radius={[10, 10, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Flex>
      </ChartFrame>
    </Flex>
  );
};
